import { FC, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";

import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";

import { MemberNotification } from "@/App/api/notification";

interface PAMessageData {
  case_id: number;
  drug_name: string;
  event_type: "case_initiated" | "case_decisioned" | "renewal_available";
  outcome?: "approved" | "denied" | "closed" | string;
  expiration_date?: string;
  customer_care_phone?: string;
  prescriber_phone?: string;
  buttons?: Array<{ type: string; label: string; phone_number: string }>;
}

export const PAMessage: FC<{ readonly memberNotification: MemberNotification }> = ({ memberNotification }) => {
  const { t: translate } = useTranslation();

  const messageData: PAMessageData | null = useMemo(() => {
    return memberNotification.json_message as PAMessageData | null;
  }, [memberNotification]);

  if (!messageData) {
    return null;
  }

  const drugName = messageData.drug_name || translate("MESSAGE_DETAIL.paMessage.yourMedication");
  const customerCarePhone = messageData.customer_care_phone || translate("MESSAGE_DETAIL.paMessage.seeIdCard");
  const prescriberPhone = messageData.prescriber_phone || translate("MESSAGE_DETAIL.paMessage.seePrescription");

  if (messageData.event_type === "renewal_available") {
    return (
      <Stack testId="pa-message-container">
        <Stack spacing="small" role="main">
          <Paragraph>
            <Trans
              i18nKey="MESSAGE_DETAIL.paMessage.renewalBody"
              values={{ drugName }}
              components={{ bold: <strong /> }}
            />
          </Paragraph>
        </Stack>

        <Stack spacing="small">
          <h3>{translate("MESSAGE_DETAIL.paMessage.whatToDoNext")}</h3>
          <ul>
            <li>{translate("MESSAGE_DETAIL.paMessage.renewalStep1")}</li>
            <li>{translate("MESSAGE_DETAIL.paMessage.renewalStep2")}</li>
          </ul>
        </Stack>

        <Stack spacing="small">
          <h3>{translate("MESSAGE_DETAIL.paMessage.needHelp")}</h3>
          <ul>
            <li>
              {translate("MESSAGE_DETAIL.paMessage.questionsAboutBenefitsCoverage")}: <strong>{customerCarePhone}</strong>
            </li>
            <li>
              {translate("MESSAGE_DETAIL.paMessage.clinicalDetailsNextSteps")}: <strong>{prescriberPhone}</strong>
            </li>
          </ul>
        </Stack>
      </Stack>
    );
  }

  /* For other PA event types (case_initiated, case_decisioned), render the HTML message.
     These are rendered by GeneralMessage since they have HTML in the message field. */
  return null;
};
